import {
  LabelType,
  DropOffTypeKey,
  DropOffsPointType,
  ShipmentEntryType,
  LabelOptionsLabelType,
  DropOffType,
  PrinterRequirement,
  PhoneRequirement,
} from '../constants'

export type LabelOptionsDropOffPrintIconDto = {
  url: string
  text: string
}

export type LabelOptionsDropOffTypeMetaDto = {
  icon_url: string
  confirm_title: string
  confirm_message: string
  secondary_title: string
  confirm_selection_title: string
  cancel_selection_title: string
  print_icon: LabelOptionsDropOffPrintIconDto | null
}

export type LabelOptionsDropOffTypesDto = {
  id: number
  instructions: string
  key: DropOffTypeKey
  title: string
  subtitle: string
  pick_up_date_shown: boolean
  pick_up_time_shown: boolean
  drop_off_type_meta: LabelOptionsDropOffTypeMetaDto
}

export type LabelOptionsAvailableLabelTypesDto = {
  type: LabelType
  title: string
  subtitle: string
  icon_url: string
}

export type LabelOptionsReceiverDto = {
  name: string
  photo_url: string
}

export type LabelOptionsSenderDto = {
  phone_number: string | null
  phone_number_requirement: PhoneRequirement
  photo_url: string
}

export type LabelOptionsCarrierDto = {
  name: string
  icon_url: string
  terms_and_conditions: string | null
}

export type LabelOptionsDropOffsDto = {
  type: DropOffsPointType
}

export type LabelOptionsDto = {
  screen_title: string
  action_title: string
  drop_off_map_enabled: boolean
  shipment_entry_type: ShipmentEntryType
  carrier: LabelOptionsCarrierDto
  sender: LabelOptionsSenderDto
  receiver: LabelOptionsReceiverDto
  available_label_types: Array<LabelOptionsAvailableLabelTypesDto> | null
  drop_off_types: Array<LabelOptionsDropOffTypesDto> | null
  info_banner_text: string | null
  info_banner_type: string | null
  drop_offs?: Array<LabelOptionsDropOffsDto>
  shipping_order_id: number | null
}

export type LabelOptionsDropOffPrintIconModel = {
  url: string
  text: string
}

export type LabelOptionsDropOffTypeMetaModel = {
  iconUrl: string
  confirmTitle: string
  confirmMessage: string
  secondaryTitle: string
  confirmSelectionTitle: string
  cancelSelectionTitle: string
  printIcon: LabelOptionsDropOffPrintIconModel | null
}

export type LabelOptionsDropOffTypesModel = {
  id: number
  instructions: string
  key: DropOffTypeKey
  title: string
  subtitle: string
  pickUpDateShown: boolean
  pickUpTimeShown: boolean
  dropOffTypeMeta: LabelOptionsDropOffTypeMetaModel
}

export type LabelOptionsAvailableLabelTypesModel = {
  type: LabelType
  title: string
  subtitle: string
  iconUrl: string
}

export type LabelOptionsReceiverModel = {
  name: string
  photoUrl: string
}

export type LabelOptionsSenderModel = {
  phoneNumber: string | null
  phoneNumberRequirement: PhoneRequirement
  photoUrl: string
}

export type LabelOptionsCarrierModel = {
  name: string
  iconUrl: string
  termsAndConditions: string | null
}

export type LabelOptionsDropOffsModel = {
  type: DropOffsPointType
}

export type LabelOptionsModel = {
  screenTitle: string
  actionTitle: string
  dropOffMapEnabled: boolean
  shipmentEntryType: ShipmentEntryType
  carrier: LabelOptionsCarrierModel
  sender: LabelOptionsSenderModel
  receiver: LabelOptionsReceiverModel
  availableLabelTypes: Array<LabelOptionsAvailableLabelTypesModel> | null
  dropOffTypes: Array<LabelOptionsDropOffTypesModel> | null
  infoBannerText: string | null
  infoBannerType: string | null
  dropOffs?: Array<LabelOptionsDropOffsModel>
  shippingOrderId: number | null
}

export type GetLabelOptionsResp = {
  label_options: LabelOptionsDto
}

export const isGetLabelOptionsResp = (
  response: GetLabelOptionsResp | GetLabelOptionsRewriteResp,
): response is GetLabelOptionsResp => {
  return 'drop_off_map_enabled' in response.label_options
}

export type LabelOptionsRewriteReceiverDto = {
  name: string
  photo_url: string | null
}

export type LabelOptionsRewriteSenderDto = {
  phone_number: string | null
  phone_number_requirement: PhoneRequirement
  photo_url: string | null
}

export type LabelOptionsRewriteCarrierDto = {
  name: string
  icon_url: string
  terms_and_conditions: string | null
}

export type LabelOptionsRewriteDropOffOptionDto = {
  type: string
  label_types: Array<LabelOptionsLabelType>
  free_reusable_packaging_available: boolean
  formatted_dimensions?: string
  contactless?: boolean
  requires_pickup_time?: boolean
  requires_pickup_date?: boolean
  printer_requirement?: PrinterRequirement
}

export type LabelOptionsRewriteDropOffsDto = {
  type: DropOffType
  provides_nearby_drop_off_points: boolean
  options: Array<LabelOptionsRewriteDropOffOptionDto>
}

export type LabelOptionsRewriteDto = {
  screen_title: string
  action_title: string
  shipment_entry_type: ShipmentEntryType
  carrier: LabelOptionsRewriteCarrierDto
  sender: LabelOptionsRewriteSenderDto
  receiver: LabelOptionsRewriteReceiverDto
  shipping_order_id: number
  drop_offs: Array<LabelOptionsRewriteDropOffsDto>
  info_banner_text?: string
  info_banner_type?: string
}

export type LabelOptionsRewriteReceiverModel = {
  name: string
  photoUrl: string | null
}

export type LabelOptionsRewriteSenderModel = {
  phoneNumber: string | null
  phoneNumberRequirement: PhoneRequirement
  photoUrl: string | null
}

export type LabelOptionsRewriteCarrierModel = {
  name: string
  iconUrl: string
  termsAndConditions: string | null
}

export type LabelOptionsRewriteDropOffOptionModel = {
  type: string
  labelTypes: Array<LabelOptionsLabelType>
  freeReusablePackagingAvailable: boolean
  formattedDimensions?: string
  contactless?: boolean
  requiresPickupTime?: boolean
  requiresPickupDate?: boolean
  printerRequirement?: PrinterRequirement
}

export type LabelOptionsRewriteDropOffsModel = {
  type: DropOffType
  providesNearbyDropOffPoints: boolean
  options: Array<LabelOptionsRewriteDropOffOptionModel>
}

export type LabelOptionsRewriteModel = {
  screenTitle: string
  actionTitle: string
  shipmentEntryType: ShipmentEntryType
  carrier: LabelOptionsRewriteCarrierModel
  sender: LabelOptionsRewriteSenderModel
  receiver: LabelOptionsRewriteReceiverModel
  shippingOrderId: number
  dropOffs: Array<LabelOptionsRewriteDropOffsModel>
  infoBannerText?: string
  infoBannerType?: string
}

export type GetLabelOptionsRewriteResp = {
  label_options: LabelOptionsRewriteDto
}
